// ─── PART 8: WATCHLIST + SIDEBAR + ROOT APP ───────────────────────────────────
// Dependencies: all previous parts
// Exports: WatchlistTab, Sidebar, default App
//
// WatchlistTab:
//   Alpaca REST snapshots — 30s polling
//   localStorage persistence under "fterm_watchlist"
//   Default symbols: SPY, QQQ, AAPL, NVDA, TSLA, GLD, TLT, USO
//
// Sidebar:
//   Treasury debt (no key, always live)
//   FRED Fed Funds (key required)
//   Key status indicators
//   Source directory
//
// Root App:
//   globalSignals state — bus between tabs and SignalBoard
//   handleData callback — memoized, passed to every tab as onData prop
//   Tab routing
//   Settings modal gate
//   Clock

import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";

// Part 1 — core
import {
  FRED_BASE, TREASURY_BASE, ALPACA_REST,
  C, s, font, fmt, pct, fmtB, fmtK,
  DataStatus, backendFetch,
} from "./core";
import { FactorExposurePanel } from "./FactorExposurePanel";

// ─── DEFAULT WATCHLIST ────────────────────────────────────────────────────────
const DEFAULT_WATCHLIST = [
  "SPY", "QQQ", "AAPL", "NVDA", "TSLA",
  "GLD", "TLT", "USO", "XLF", "HYG",
];

const WATCHLIST_KEY = "fterm_watchlist";

// ─── POSITION SIZING CALCULATOR ───────────────────────────────────────────────
// ⚠ REVISED 2026-08-15: the budget IS persisted now. It was originally left out so
// the feature could not be mistaken for a portfolio record, but the calculator
// collapsed on every navigation away from this tab, and the Convergence Board
// summary needs the figure to show a Remaining line at all. It is stored as a
// CALCULATOR SETTING — clearing the field deletes the key outright, and nothing
// here records fills, cost basis or a broker link, so no P&L is derivable from it.
// ⚠⚠ BOTH KEYS ARE DUPLICATED IN ConvergenceBoard.jsx, which reads them to render
// the position-sizing summary. Nothing enforces that they stay in step — if either
// name changes here, change it there too.
const POSITION_SIZES_KEY = "qs_position_sizes";     // { TICKER: shares }
const CALC_BUDGET_KEY    = "qs_calculator_budget";  // string, as typed

const readSizes = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(POSITION_SIZES_KEY));
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out = {};
    // ⚠ Coerced and clamped on READ, not just on write — localStorage is
    // user-editable and a junk value would otherwise reach the arithmetic and
    // render NaN across the whole table.
    Object.entries(raw).forEach(([sym, v]) => {
      // ⚠⚠ TYPE-CHECK BEFORE Number(). null, "", false and [] ALL coerce to 0,
      // so a corrupt entry would survive as a legitimate "0 shares" — and because
      // the seeding effect skips any ticker that already has a key, that row could
      // never be sized again. Rejecting the value instead lets the default reseed.
      const numeric = typeof v === "number"
        || (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)));
      if (!numeric) return;
      const n = Math.floor(Number(v));
      if (Number.isFinite(n) && n >= 0) out[String(sym).toUpperCase()] = n;
    });
    return out;
  } catch {
    return {};
  }
};

// Whole dollars for the running total; the per-row price keeps the table's 2dp.
const usd = (n) =>
  !Number.isFinite(n) ? "—"
    : `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const DISCLAIMER =
  "This is a calculator only. Share counts are based on current market prices and " +
  "your inputs. This is not investment advice, a recommendation, or a solicitation " +
  "to buy or sell any security.";

// ─── WATCHLIST TAB ────────────────────────────────────────────────────────────
export function WatchlistTab({ keys, globalSignals, onData }) {
  const [watchlist,   setWatchlist]   = useState(() => {
    try {
      const saved = localStorage.getItem(WATCHLIST_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_WATCHLIST;
    } catch {
      return DEFAULT_WATCHLIST;
    }
  });
  const [input,       setInput]       = useState("");
  const [quotes,      setQuotes]      = useState({});
  const [status,      setStatus]      = useState("offline");
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const [sortBy,      setSortBy]      = useState("symbol"); // symbol | change | volume
  // ── Position sizing calculator ──
  const [budget, setBudgetState] = useState(() => {
    try { return localStorage.getItem(CALC_BUDGET_KEY) || ""; } catch { return ""; }
  });
  const [sizes,  setSizes]  = useState(readSizes);     // { TICKER: shares }, persisted
  // ⚠ Minimize is a TRANSIENT view preference and is deliberately NOT persisted.
  // Storing it would return a user to a collapsed calculator with no visible cause
  // — the budget and sizes are still there, so nothing on screen would explain why
  // the table is gone. Resets to expanded on every mount.
  const [calcMin, setCalcMin] = useState(false);

  // ⚠ An empty field REMOVES the key rather than storing "". A stored empty string
  // is indistinguishable from "never set" to every reader, and leaving one behind
  // would have the Convergence Board summary carry a dead entry it must then
  // special-case. Clearing the field is the user closing the calculator.
  const setBudget = (v) => {
    setBudgetState(v);
    try {
      if (v === "") localStorage.removeItem(CALC_BUDGET_KEY);
      else localStorage.setItem(CALC_BUDGET_KEY, v);
    } catch { /* quota / private mode — the field still works in-session */ }
  };

  const saveList = (list) => {
    setWatchlist(list);
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(list));
  };

  const saveSizes = (next) => {
    setSizes(next);
    try { localStorage.setItem(POSITION_SIZES_KEY, JSON.stringify(next)); } catch { /* quota */ }
  };

  const add = () => {
    const sym = input.trim().toUpperCase();
    if (sym && !watchlist.includes(sym)) saveList([...watchlist, sym]);
    setInput("");
  };

  const remove = (sym) => {
    saveList(watchlist.filter((s) => s !== sym));
    // ⚠ Drop the stored size with the ticker. Without this a symbol re-added later
    // silently inherits a share count from a budget the user no longer remembers
    // setting, and the orphan survives in localStorage indefinitely.
    if (Object.prototype.hasOwnProperty.call(sizes, sym)) {
      const next = { ...sizes };
      delete next[sym];
      saveSizes(next);
    }
  };

  // ── Calculator derivations ──
  const budgetNum = Number.parseFloat(budget);
  const calcOpen  = Number.isFinite(budgetNum) && budgetNum > 0;
  const priceOf   = (sym) => {
    const p = quotes[sym]?.price;
    return typeof p === "number" && Number.isFinite(p) && p > 0 ? p : null;
  };
  const equalShares = (price, n) =>
    price && n > 0 ? Math.max(0, Math.floor(budgetNum / n / price)) : null;

  // Seed a default ONLY for tickers that have no stored size.
  // ⚠⚠ STORED WINS OVER THE DEFAULT. Re-seeding on every budget change would
  // silently discard the user's edits, which is the whole point of persisting them
  // — RESET TO EQUAL WEIGHT is the deliberate way to re-seed. The `changed` guard
  // matters too: `quotes` is a fresh object on every 30s poll, so without it this
  // effect would rewrite localStorage on a timer forever.
  useEffect(() => {
    if (!calcOpen || watchlist.length === 0) return;
    const n = watchlist.length;
    let changed = false;
    const next = { ...sizes };
    for (const sym of watchlist) {
      if (Object.prototype.hasOwnProperty.call(next, sym)) continue;
      const shares = equalShares(priceOf(sym), n);
      if (shares == null) continue;   // no price yet — leave unsized, shown as "—"
      next[sym] = shares;
      changed = true;
    }
    if (changed) saveSizes(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcOpen, budgetNum, watchlist, quotes, sizes]);

  // ⚠⚠ THE ONLY DESTRUCTIVE ACTION ON THE CALCULATOR, AND IT IS NAMED FOR IT.
  // This replaced a button labelled CLOSE that cleared the BUDGET only — share
  // counts survived in localStorage and kept driving the Convergence Board's
  // position-sizing summary after the user believed they had closed the thing.
  // Clearing now means clearing: budget, every share count, and both keys.
  // ⚠ removeItem, not saveSizes({}) — an empty object is a stored value and would
  // leave "{}" behind as a live entry rather than removing the key as specified.
  const clearCalculator = () => {
    setBudget("");                        // also removes CALC_BUDGET_KEY
    setSizes({});
    try { localStorage.removeItem(POSITION_SIZES_KEY); } catch { /* private mode */ }
    setCalcMin(false);                    // a fresh budget should open expanded
  };

  const resetEqualWeight = () => {
    if (!calcOpen || watchlist.length === 0) return;
    const n = watchlist.length;
    const next = {};
    for (const sym of watchlist) {
      const shares = equalShares(priceOf(sym), n);
      if (shares != null) next[sym] = shares;
    }
    saveSizes(next);
  };

  const setShares = (sym, raw) => {
    // Digits only. An emptied field lands on 0 and self-corrects as the user types
    // over it ("0" + "25" parses back to 25).
    const digits = String(raw).replace(/[^0-9]/g, "");
    const v = digits === "" ? 0 : Math.max(0, Number.parseInt(digits, 10) || 0);
    saveSizes({ ...sizes, [sym]: v });
  };

  // ⚠ Rows without a price are excluded from the total rather than counted as 0 —
  // "unknown" and "nothing allocated" are different, and folding one into the other
  // would understate the allocation against the budget.
  const allocated = watchlist.reduce((acc, sym) => {
    const price = priceOf(sym);
    const sh = sizes[sym];
    return price && Number.isFinite(sh) ? acc + sh * price : acc;
  }, 0);
  const remaining = calcOpen ? budgetNum - allocated : 0;

  // ⚠ Same rule the Convergence Board summary uses — shares > 0, intersected with
  // the CURRENT watchlist — so the two counts can never disagree. A 0-share entry
  // is a real stored value but not a position, and an orphan left by a ticker
  // removed before remove() started clearing sizes is not on the list any more.
  const sizedCount = watchlist.reduce(
    (acc, sym) => acc + ((sizes[sym] || 0) > 0 ? 1 : 0), 0);

  const fetchQuotes = useCallback(async () => {
    if (!window.QUANT_SIGNAL_BACKEND || watchlist.length === 0) return;
    try {
      const res = await backendFetch(
        `${ALPACA_REST}/stocks/snapshots?symbols=${watchlist.join(",")}`,
        `/api/snapshot?symbols=${watchlist.join(",")}`
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const q = {};
      Object.entries(data).forEach(([sym, snap]) => {
        const price     = snap.latestTrade?.p;
        const prevClose = snap.prevDailyBar?.c;
        q[sym] = {
          price,
          prevClose,
          changePct:  price && prevClose ? ((price - prevClose) / prevClose) * 100 : null,
          changeAbs:  price && prevClose ? price - prevClose : null,
          volume:     snap.dailyBar?.v ?? null,
          vwap:       snap.dailyBar?.vw ?? null,
          high:       snap.dailyBar?.h ?? null,
          low:        snap.dailyBar?.l ?? null,
        };
      });
      setQuotes(q);
      setStatus("live");
      setLastUpdate(new Date().toLocaleTimeString());
    } catch {
      setStatus("offline");
    }
  }, [keys, watchlist]);

  useEffect(() => {
    fetchQuotes();
    const t = setInterval(fetchQuotes, 30_000);
    return () => clearInterval(t);
  }, [fetchQuotes]);

  // Sorted list
  const sorted = [...watchlist].sort((a, b) => {
    if (sortBy === "change") {
      const ca = quotes[a]?.changePct ?? -999;
      const cb = quotes[b]?.changePct ?? -999;
      return cb - ca;
    }
    if (sortBy === "volume") {
      const va = quotes[a]?.volume ?? 0;
      const vb = quotes[b]?.volume ?? 0;
      return vb - va;
    }
    return a.localeCompare(b); // symbol
  });

  if (!keys.alpacaKey && !window.QUANT_SIGNAL_BACKEND) {
    return (
      <div style={{ ...s.panel, ...s.panelB, color: C.textDim, fontSize: 14 }}>
        <AlertCircle size={11} style={{ marginRight: 6, verticalAlign: "middle" }} />
        Data unavailable.
      </div>
    );
  }

  return (
    <div>
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>WATCHLIST</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <DataStatus status={status} source="30s" timestamp={lastUpdate} />
            <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={fetchQuotes}>
              <RefreshCw size={10} />
            </button>
          </div>
        </div>

        <div style={s.panelB}>
          {/* ─── POSITION SIZING CALCULATOR ─── */}
          {/* Sits above the add/sort controls so those stay attached to the table
              they operate on. Collapsed to a single input until a budget is
              entered — no empty rows, no placeholder table. */}
          <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: C.textDim }}>
                POSITION SIZING CALCULATOR
              </span>
              {/* Minimized: the state summary replaces the prompt, so a collapsed
                  calculator still says what it is holding. */}
              <span style={{ fontSize: 10, color: C.textDim }}>
                {calcOpen && calcMin
                  ? `${usd(budgetNum)} budget · ${sizedCount} position${sizedCount === 1 ? "" : "s"} sized`
                  : "Optional — enter a budget to size your watchlist"}
              </span>
              {calcOpen && (
                <span style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
                  {/* ⚠ Glyph stays "─" in both states as specified. The title
                      attribute is what distinguishes the action, so the control
                      does not silently change shape under the cursor. */}
                  <button
                    style={{ ...s.btn(), padding: "1px 8px", fontSize: 11, lineHeight: 1.4 }}
                    title={calcMin ? "Expand calculator" : "Minimize calculator"}
                    aria-label={calcMin ? "Expand calculator" : "Minimize calculator"}
                    aria-expanded={!calcMin}
                    onClick={() => setCalcMin((m) => !m)}
                  >
                    ─
                  </button>
                  <button
                    style={{ ...s.btn(), padding: "1px 8px", fontSize: 11, lineHeight: 1.4 }}
                    title="Clear budget and all share counts"
                    aria-label="Clear budget and all share counts"
                    onClick={clearCalculator}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>

            {/* Minimized collapses to the header line alone — the budget input goes
                with the table, since editing a budget you cannot see the effect of
                is worse than expanding first. */}
            {!(calcOpen && calcMin) && (
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  style={{ ...s.inp, width: 150 }}
                  placeholder="Enter budget ($)"
                  inputMode="decimal"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g, ""))}
                />
                {calcOpen && (
                  <button style={{ ...s.btn(), padding: "3px 8px", fontSize: 11 }} onClick={resetEqualWeight}>
                    RESET TO EQUAL WEIGHT
                  </button>
                )}
              </div>
            )}

            {calcOpen && !calcMin && (
              <div style={{ marginTop: 10 }}>
                {/* ⚠ NON-DISMISSIBLE AND ABOVE THE NUMBERS, DELIBERATELY. It must be
                    read before the share counts, and there is no state that can
                    hide it. Do not add a collapse toggle. */}
                <div style={{ fontSize: 9, color: C.textDim, lineHeight: 1.55, marginBottom: 8 }}>
                  {DISCLAIMER}
                </div>

                {/* ⚠ Same rule as the quotes table below: scroller inside the
                    panel, so the panel background travels with the scroll. */}
                <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 360, borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {[["TICKER", "left"], ["PRICE", "right"], ["SHARES", "right"], ["VALUE", "right"]].map(([h, align]) => (
                        <th key={h} style={{
                          padding: "3px 8px", textAlign: align, fontSize: 10, color: C.textDim,
                          fontWeight: 700, letterSpacing: "0.06em", whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((sym) => {
                      const price  = priceOf(sym);
                      const sh     = sizes[sym];
                      const hasSh  = Number.isFinite(sh);
                      const value  = price && hasSh ? sh * price : null;
                      return (
                        <tr key={`calc-${sym}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "3px 8px", fontWeight: 700, color: C.text }}>{sym}</td>
                          <td style={{ padding: "3px 8px", textAlign: "right", color: price ? C.text : C.textDim }}>
                            {price ? `$${fmt(price)}` : "—"}
                          </td>
                          <td style={{ padding: "3px 8px", textAlign: "right" }}>
                            {price ? (
                              <input
                                aria-label={`${sym} shares`}
                                style={{ ...s.inp, width: 70, textAlign: "right", padding: "2px 6px", fontSize: 12 }}
                                inputMode="numeric"
                                value={hasSh ? String(sh) : ""}
                                onChange={(e) => setShares(sym, e.target.value)}
                              />
                            ) : (
                              <span style={{ color: C.textDim }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "3px 8px", textAlign: "right", color: value != null ? C.text : C.textDim }}>
                            {value != null ? usd(value) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", marginTop: 8, fontSize: 12 }}>
                  <span style={{ color: C.textDim }}>
                    Allocated: <span style={{ color: C.text, fontWeight: 700 }}>{usd(allocated)}</span>
                  </span>
                  <span style={{ color: C.textDim }}>
                    Remaining:{" "}
                    <span style={{ color: remaining < 0 ? C.red : C.text, fontWeight: 700 }}>
                      {usd(remaining)}
                    </span>
                  </span>
                </div>
                {remaining < 0 && (
                  <div style={{ fontSize: 9, color: C.red, textAlign: "right", marginTop: 2 }}>
                    Over budget
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add ticker row */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <input
              style={{ ...s.inp, flex: 1, maxWidth: 160 }}
              placeholder="Add ticker (e.g. AAPL)"
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <button style={s.btn("amber")} onClick={add}>ADD</button>
            <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
              {[["symbol", "A-Z"], ["change", "CHG%"], ["volume", "VOL"]].map(([key, label]) => (
                <button
                  key={key}
                  style={{ ...s.btn(sortBy === key ? "teal" : "default"), padding: "3px 8px", fontSize: 11 }}
                  onClick={() => setSortBy(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {/* ⚠⚠ THE SCROLLER IS INSIDE s.panel, NOT AROUND IT. s.panel paints the
              background and border; a table wider than the panel used to overflow
              that painted box entirely, so cells rendered on bare app background —
              the "text floating with no container" in the mobile report. With the
              scroller nested here the panel keeps its own viewport-width box, the
              background and border stay put, and only the table moves inside them.
              Wrapping s.panel itself would put the growth back one level up.
              ⚠ The overflow belongs on the WRAPPER and nothing else. One element
              cannot both scroll and hold the columns — it would have to be
              narrower than its own content and wider than it at the same time. */}
          <div style={{ overflowX: "auto" }}>
          {/* ⚠⚠ NO minWidth, AND THE ABSENCE IS THE MEASUREMENT. This carried
              minWidth: 560, derived from advance-width arithmetic. Measured on a
              real device (411px viewport, 366px container, three rows of quotes):
              the eight columns' own content floor is 642px. The 560 was 15% low
              and therefore never once applied — the table was always going to be
              642 and always going to scroll. A constant that cannot fire is worse
              than no constant, because the next reader takes it for a measurement.
              ⚠ Removing it is not neutral, it is better: with an empty watchlist
              the tbody is empty, and a 560px floor would have forced a horizontal
              scroll across a table with nothing in it. Now it is exactly as wide
              as it needs to be and scrolls only when it must.
              ⚠ Same call as UnusualTable in OptionsTab — eight columns of market
              data exceed a phone panel unaided, so the content decides. */}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {[
                  ["TICKER",     "left"],
                  ["PRICE",      "right"],
                  ["CHG%",       "right"],
                  ["CHG$",       "right"],
                  ["DAY H/L",    "right"],
                  ["VWAP",       "right"],
                  ["VOLUME",     "right"],
                  ["",           "right"],
                ].map(([h, align]) => (
                  <th key={h} style={{
                    padding: "3px 8px",
                    textAlign: align,
                    fontSize: 11,
                    color: C.textDim,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((sym) => {
                const q  = quotes[sym];
                const up = q?.changePct != null && q.changePct >= 0;
                return (
                  <tr
                    key={sym}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      transition: "background 0.1s",
                    }}
                  >
                    {/* Ticker */}
                    <td style={{ padding: "8px 8px", fontWeight: 700, fontSize: 17, color: C.text }}>
                      {sym}
                    </td>
                    {/* Price */}
                    <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700 }}>
                      {q?.price ? `$${fmt(q.price)}` : "—"}
                    </td>
                    {/* Change % */}
                    <td style={{ padding: "8px 8px", textAlign: "right" }}>
                      {q?.changePct != null
                        ? <span style={s.badge(up)}>{pct(q.changePct)}</span>
                        : <span style={{ color: C.textDim }}>—</span>
                      }
                    </td>
                    {/* Change $ */}
                    <td style={{ padding: "8px 8px", textAlign: "right", color: q?.changeAbs != null ? (q.changeAbs >= 0 ? C.green : C.red) : C.textDim, fontSize: 13 }}>
                      {q?.changeAbs != null
                        ? `${q.changeAbs >= 0 ? "+" : ""}${fmt(q.changeAbs)}`
                        : "—"}
                    </td>
                    {/* Day H/L */}
                    <td style={{ padding: "8px 8px", textAlign: "right", fontSize: 13, color: C.textDim }}>
                      {q?.high && q?.low
                        ? <span><span style={{ color: C.green }}>${fmt(q.high)}</span> / <span style={{ color: C.red }}>${fmt(q.low)}</span></span>
                        : "—"}
                    </td>
                    {/* VWAP */}
                    <td style={{ padding: "8px 8px", textAlign: "right", fontSize: 13, color: C.textDim }}>
                      {q?.vwap ? `$${fmt(q.vwap)}` : "—"}
                    </td>
                    {/* Volume */}
                    <td style={{ padding: "8px 8px", textAlign: "right", fontSize: 13, color: C.textMid }}>
                      {fmtK(q?.volume)}
                    </td>
                    {/* Remove */}
                    <td style={{ padding: "8px 8px", textAlign: "right" }}>
                      <button
                        style={{ ...s.btn(), padding: "2px 6px", fontSize: 11, color: C.red }}
                        onClick={() => remove(sym)}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {watchlist.length === 0 && (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              Add tickers above to begin tracking
            </div>
          )}

          <div style={{ fontSize: 11, color: C.textDim, marginTop: 8 }}>
            {watchlist.length} symbols · Refreshed every 30s · VWAP = Volume Weighted Average Price ·
            Sort by ticker, change%, or volume using buttons above.
          </div>
        </div>
      </div>

      {/* Mini performance heatmap */}
      {Object.keys(quotes).length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>PERFORMANCE HEATMAP</span>
          </div>
          <div style={{ padding: "10px 10px" }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {sorted
                .filter((sym) => quotes[sym]?.changePct != null)
                .map((sym) => {
                  const chg       = quotes[sym].changePct;
                  const up        = chg >= 0;
                  const intensity = Math.min(1, Math.abs(chg) / 3);
                  const bg        = up
                    ? `rgba(34,197,94,${0.08 + intensity * 0.3})`
                    : `rgba(239,68,68,${0.08 + intensity * 0.3})`;
                  return (
                    <div key={sym} style={{
                      background: bg,
                      border: `1px solid ${up ? C.green : C.red}44`,
                      borderRadius: 2,
                      padding: "6px 10px",
                      minWidth: 64,
                      textAlign: "center",
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{sym}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: up ? C.green : C.red }}>
                        {pct(chg)}
                      </div>
                      <div style={{ fontSize: 11, color: C.textDim }}>
                        ${fmt(quotes[sym].price)}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Factor Exposure Dashboard (Phase 2) — below the quote table */}
      <FactorExposurePanel
        symbols={watchlist}
        globalSignals={globalSignals}
        onData={onData}
      />
    </div>
  );
}

// ─── NAV ITEM ─────────────────────────────────────────────────────────────────
function NavItem({ label, active, onClick, mobile }) {
  const [hover, setHover] = useState(false);
  const downRef = useRef(null); // pointerdown position, to tell a tap from a scroll
  // Mobile: navigate on a genuine TAP (pointerup within a few px of pointerdown),
  // not on raw pointerdown — so dragging to scroll the nav list does NOT navigate
  // or close the drawer. We do NOT preventDefault on pointerdown, so the list
  // stays vertically scrollable. Still one tap, no click delay. Desktop unchanged.
  const TAP_SLOP = 10; // px of movement allowed before it counts as a scroll, not a tap
  const handlers = mobile
    ? {
        onPointerDown: (e) => { downRef.current = { x: e.clientX, y: e.clientY }; },
        onPointerUp: (e) => {
          const d = downRef.current;
          downRef.current = null;
          if (!d) return;
          if (Math.abs(e.clientX - d.x) <= TAP_SLOP && Math.abs(e.clientY - d.y) <= TAP_SLOP) {
            e.stopPropagation();
            onClick();
          }
        },
      }
    : {
        onClick,
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false),
      };
  return (
    <div
      {...handlers}
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: mobile ? "12px 14px" : "8px 12px",
        minHeight: mobile ? 44 : undefined,
        display: mobile ? "flex" : undefined,
        alignItems: mobile ? "center" : undefined,
        boxSizing: "border-box",
        cursor: "pointer",
        color: active ? C.amber : hover ? C.text : C.textMid,
        background: hover ? C.panelAlt : "transparent",
        borderLeft: `2px solid ${active ? C.amber : "transparent"}`,
        whiteSpace: "nowrap",
        letterSpacing: "0.06em",
      }}
    >
      {label}
    </div>
  );
}

// ─── SIDEBAR (vertical navigation) ────────────────────────────────────────────
export function Sidebar({ keys, tabs = [], activeTab, onSelect, mobile = false }) {
  const [debt,    setDebt]    = useState(null);
  const [fedRate, setFedRate] = useState(null);
  const [debtTs,  setDebtTs]  = useState(null);

  useEffect(() => {
    // National debt — always live, no key (routed through backend to avoid CORS)
    backendFetch(
      `${TREASURY_BASE}/accounting/od/debt_to_penny` +
      `?fields=tot_pub_debt_out_amt,record_date` +
      `&sort=-record_date&page[size]=1`,
      "/api/treasury/debt"
    )
      .then((r) => r.json())
      .then((d) => {
        // Backend shape: { amount, date }. Direct Treasury shape: { data:[{...}] }.
        const amount = d.amount != null
          ? d.amount
          : (d.data?.[0] ? parseFloat(d.data[0].tot_pub_debt_out_amt) : null);
        const date = d.date != null ? d.date : d.data?.[0]?.record_date;
        if (amount != null) {
          setDebt(amount);
          setDebtTs(date);
        }
      })
      .catch(() => {});

    // Fed Funds — needs a Federal Reserve feed
    if (keys.fredKey || window.QUANT_SIGNAL_BACKEND) {
      backendFetch(
        `${FRED_BASE}?series_id=FEDFUNDS` +
        `&api_key=${keys.fredKey}&file_type=json&limit=1&sort_order=desc`,
        `/api/fred/FEDFUNDS?limit=1`
      )
        .then((r) => r.json())
        .then((d) => {
          if (d.observations?.[0]?.value != null) setFedRate(parseFloat(d.observations[0].value));
        })
        .catch(() => {});
    }
  }, [keys.fredKey]);

  return (
    <div style={{
      width: 200,
      minWidth: 200,
      height: "100%",
      background: C.panel,
      borderRight: `1px solid ${C.border}`,
      overflowY: "auto",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Navigation — grouped by desk. `tabs` is [{ label, items }]; a null label
          is an unlabeled group (top-level / utility). A separator sits above every
          group after the first; desk labels are non-clickable section headers. */}
      <div style={{ padding: "8px 0" }}>
        {tabs.map((group, gi) => (
          <div key={group.label || `grp-${gi}`}>
            {gi > 0 && <div style={{ borderTop: `1px solid ${C.border}`, margin: "6px 10px" }} />}
            {group.label && (
              <div style={{
                fontSize: 9, fontWeight: 700, color: C.textDim,
                letterSpacing: "0.1em", textTransform: "uppercase",
                padding: "2px 14px 3px",
              }}>
                {group.label}
              </div>
            )}
            {(group.items || []).map((t) => (
              <NavItem key={t} label={t} active={activeTab === t} onClick={() => onSelect(t)} mobile={mobile} />
            ))}
          </div>
        ))}
      </div>

      {/* Quick stats */}
      <div style={{ padding: "10px 12px", borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.amber, letterSpacing: "0.12em", marginBottom: 8, fontWeight: 700 }}>
          QUICK STATS
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={s.lbl}>NATIONAL DEBT</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>
            {debt ? fmtB(debt) : "Loading..."}
          </div>
          <div style={{ fontSize: 10, color: C.textDim }}>{debtTs || ""}</div>
        </div>
        <div>
          <div style={s.lbl}>FED FUNDS RATE</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            {fedRate != null ? `${fmt(fedRate)}%` : keys.fredKey ? "Loading..." : "—"}
          </div>
          <div style={{ fontSize: 10, color: C.textDim }}>Monthly</div>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{
        borderTop: `1px solid ${C.border}`,
        padding: "8px 12px",
        marginTop: "auto",
        fontSize: 11,
        color: "#FFD700",
        fontWeight: 700,
        lineHeight: 1.65,
      }}>
        Not a recommendation. For financial decisions, consult a licensed financial advisor.
      </div>
    </div>
  );
}

// ─── SOURCES TAB ──────────────────────────────────────────────────────────────
// Providers and institutions behind the terminal's data — names and coverage
// only, no implementation detail.
